/**
 * Selection anchors for presence (spec "Selection anchors for presence"): the local selection as JSON text cursors a
 * peer can resolve in its own Doc, and the resolver for a peer's anchor. y-protocols awareness `cursor.anchor/head`,
 * y-prosemirror `absolutePositionToRelativePosition` / `relativePositionToAbsolutePosition`.
 */
import type { Block, Blocks } from '@w/content-blocks'
import { TextCursor, type Doc, type TextCursorJSON } from '@w/crdt'
import { pageDocument } from '../doc-bindings-for-page'
import { indexViewBlocks } from '../doc-blocks'
import { anchoredPoint, textAnchor, type CorePoint, type CoreState } from './state'
import { locate, plain, rowBlock, rows } from './tree-read'

/** One endpoint: the cursor that follows its character, plus the point it named when published. */
export interface SelectionAnchorPoint { cursor: TextCursorJSON; hint: CorePoint }
export interface SelectionAnchors { anchor: SelectionAnchorPoint; focus: SelectionAnchorPoint }
/** `pending`: the cursor did not resolve, so the point is the hint clamped to its block's text. */
export interface ResolvedSelectionAnchor { point: CorePoint; pending: boolean }

/** The state's text selection as anchors; null for no selection, a node, table or prompt-field selection, or a point outside text. */
export function selectionAnchors(state: CoreState): SelectionAnchors | null {
  const selection = state.selection
  if (!selection || selection.nodes || selection.table || selection.field) return null
  const { anchor, focus } = selection
  const entries = rows(state.blocks)
  const [from, to] = [locate(entries, anchor), locate(entries, focus)]
  // Same association as saveSelection (spec Core contract extensions): a caret and a range start 'right', a range end 'left'.
  const order = Math.sign(from.row - to.row || from.offset - to.offset)
  // A rendered state can trail the live Doc (a later commit not yet rendered): a point past its leaf's live text is not published.
  const anchorCursor = liveCursor(state.doc, anchor, order > 0 ? 'left' : 'right')
  const focusCursor = liveCursor(state.doc, focus, order < 0 ? 'left' : 'right')
  if (!anchorCursor || !focusCursor) return null
  return {
    anchor: { cursor: anchorCursor.toJSON(), hint: { blockId: anchor.blockId, offset: anchor.offset } },
    focus: { cursor: focusCursor.toJSON(), hint: { blockId: focus.blockId, offset: focus.offset } },
  }
}

/** Equal anchors (same cursors and hints): a store keeps its snapshot identity for them. */
export function sameSelectionAnchors(a: SelectionAnchors | null, b: SelectionAnchors | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** Where a peer's anchor is in `doc` now; null (hidden) for a malformed cursor, another epoch or a missing hint block. */
export function resolveSelectionAnchor(doc: Doc, anchor: SelectionAnchorPoint): ResolvedSelectionAnchor | null {
  try {
    const point = anchoredPoint(doc, TextCursor.fromJSON(anchor.cursor))
    if (point) return { point, pending: false }
  } catch {
    return null
  }
  const length = textLength(doc, anchor.hint.blockId)
  return length === null ? null : { point: { blockId: anchor.hint.blockId, offset: Math.min(Math.max(anchor.hint.offset, 0), length) }, pending: true }
}

/** The live text length of chars leaf `blockId` in `doc`; null when it is not a chars leaf there. */
function textLength(doc: Doc, blockId: string): number | null {
  const block = indexViewBlocks(pageDocument(doc)).get(blockId)?.block
  // A chars view block is the content-blocks `chars` shape (doc-blocks PageBlock is its untyped projection).
  return block?.type === 'chars' ? plain(block as unknown as Block).length : null
}

/** The cursor for `point` when its offset is inside its chars leaf's live text; null otherwise. A leaf in a row of the
 * live view is read from its rows (tree-read `rowBlock`); one elsewhere (a field) from the live view's index. */
function liveCursor(doc: Doc, point: CorePoint, assoc: 'left' | 'right'): TextCursor | null {
  const leaf = rowBlock(pageDocument(doc).content.children as unknown as Blocks, point.blockId)
  if (!leaf) {
    const length = textLength(doc, point.blockId)
    return length !== null && point.offset >= 0 && point.offset <= length ? textAnchor(doc, point, assoc) : null
  }
  if (leaf.block.type !== 'chars' || point.offset < 0 || point.offset > plain(leaf.block).length) return null
  return doc.textCursor(`${leaf.path}.text`, point.offset, assoc)
}
